import { AlignmentRules } from '../alignmentRules';
import { Block } from '../block';
import { Bounding } from '../../../core/models/bounding';
import { Element } from '../../../equipment/models/elements/element';
import { EquipmentBase } from '../equipment-base';
import { Extension } from '../../../equipment/models/elements/extension';
import { Figure } from '../../../equipment/models/figures/figure';
import { ICanPutProductsResult } from '../canPutProductsResult';
import { PlaneElement } from './planeElement';
import { Point } from '../../../core/models/point';
import { ProductErrors, ProductGroup } from '../product-group';
import { ShelfElement } from './shelfElement';

import { Utils } from '../../../core/services/utils.service';
import { GlobalSettings } from '../../../core/models/globalSettings';
import * as jsts from 'jsts';

const GEOMETRY_FACTORY: jsts.geom.GeometryFactory = new jsts.geom.GeometryFactory();

export class BoxElement extends PlaneElement {

	get Depth(): number {
		const bounding: Bounding = this.getBoundingZ();
		return Utils.round(bounding.max - bounding.min - this.shapes[0].wallWidth * 2);
	}

	get anchorYCoordinate(): number {
		return this.shapes[0].begin.y;
	}

	get anchorZCoordinate(): number {
		return this.layoutBoundingZ.min;
	}

	get layoutBoundingX(): Bounding {
		const bounding: Bounding = this.getBoundingX();

		return {
			min: Utils.round(bounding.min + this.shapes[0].wallWidth),
			max: Utils.round(bounding.max - this.shapes[0].wallWidth),
		}
	}

	get layoutBoundingY(): Bounding {
		const bounding: Bounding = this.getBoundingY();

		return {
			min: bounding.min,
			max: bounding.min
		}
	}

	get layoutBoundingZ(): Bounding {
		const bounding: Bounding = this.getAbsoluteBoundingZ();

		return {
			min: Utils.round(bounding.min + this.shapes[0].wallWidth),
			max: Utils.round(bounding.max - this.shapes[0].wallWidth),
		}
	}

	get layoutZCoordinates(): Array<Point> {
		return this._getZCoordinates(this.layoutBoundingX, this.layoutBoundingZ);
	}

	get zCoordinates(): Array<Point> {
		return this._getZCoordinates(this.getBoundingX(), this.getBoundingZ());
	}

	get zPolygon(): jsts.geom.Polygon {
		return GEOMETRY_FACTORY.createPolygon(this.zCoordinates.map(i => new jsts.geom.Coordinate(i.x, i.y)));
	}

	constructor(element: Element) {
		super(element);
	}

	private _getZCoordinates(boundingX: Bounding, boundingZ: Bounding): Array<Point> {
		const angleRad: number = (0 * Math.PI) / 180; // TODO: need put box angle
		const cosAngle: number = Math.cos(angleRad);
		const sinAngle: number = Math.sin(angleRad);
		const width: number = Utils.round(boundingX.max - boundingX.min);
		const depth: number = Utils.round(boundingZ.max - boundingZ.min);
		const halfWidth = Utils.round(width / 2);
		const halfHeight = Utils.round(-depth / 2);
		const centerPoint: Point = { // TODO: need calculate like product group coordinates
			x: Utils.round(boundingX.min + width / 2),
			y: Utils.round(boundingZ.min + depth  / 2)
		};

		const corners: Array<Point> = [
			{ x: -halfWidth, y: -halfHeight },
			{ x: halfWidth, y: -halfHeight },
			{ x: halfWidth, y: halfHeight },
			{ x: -halfWidth, y: halfHeight },
			{ x: -halfWidth, y: -halfHeight }
		];

		return corners.map((corner: Point) => {
			const xRot = Utils.round(centerPoint.x + (corner.x * cosAngle - corner.y * sinAngle));
			const yRot = Utils.round(centerPoint.y + (corner.x * sinAngle + corner.y * cosAngle));
			return { x: xRot, y: yRot };
		});
	}

	copy(): BoxElement {
		const shapes: Array<Figure> = this.shapes.map((shape: Figure) => shape.copy()),
			extensions: Array<Extension<any>> = this.extensions.map((extension: Extension<any>) => extension.copy()),
			element: Element = new Element(this.parent, shapes, extensions, this.depend, this.id, this.idx);

		return new BoxElement(element);
	}

	getSiblingSegments(): Array<Bounding> {
		return [{
			min: Utils.round(this.getBoundingX().min + this.shapes[0].wallWidth),
			max: Utils.round(this.getBoundingX().max - this.shapes[0].wallWidth)
		}];
	}

	isNeedRemoveProducts(newProduct: ProductGroup): boolean {
		return newProduct.isLooseForm || this.products
			.some((group: Array<ProductGroup>) => group.some((pg: ProductGroup) => pg.isLooseForm));
	}

	normalize(stand: EquipmentBase, segments: Array<Bounding> = []): void {
		const boxSegments: Array<Bounding> = segments.length
				? segments
				: stand.alignment !== 'default' ? this.getBlockSegments : this.getSiblingSegments(),
			boxLimits: Bounding = this.getSiblingSegments()[0],
			zGroups: Array<Array<ProductGroup>> = this.products;

		if (!zGroups.length) return;

		if (zGroups.length == 1 && zGroups[0][0].isLooseForm) {
			zGroups[0][0].begin.x = boxLimits.min;
			zGroups[0][0].begin.z = this.layoutBoundingZ.min;
			return;
		}

		// sort products and shelf segments according to selected alignment
		if (stand.alignment === 'right') {
			zGroups.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => {
				return Element.calculateGroupMax(b) - Element.calculateGroupMax(a);
			});
			boxSegments.sort((a: Bounding, b: Bounding) => b.max - a.max);
		} else {
			zGroups.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => {
				return Element.calculateGroupMin(a) - Element.calculateGroupMin(b);
			});
		}

		if (!stand.arbitraryLayout) {
			boxSegments.forEach((segment: Bounding) => {
				// filter products which belongs to current segment, find previous product on pallets
				const segGroups: Array<Array<ProductGroup>> = zGroups.filter((group: Array<ProductGroup>, idx: number) => {
						const align: AlignmentRules = Element.alignmentRules(zGroups, idx, segment);
						return align[stand.alignment].checkAffiliation(boxLimits);
					}),
					firstGroupIndex: number = segGroups.length ? zGroups.findIndex(group => group == segGroups[0]) : 0,
					prevProduct: Bounding = firstGroupIndex
						? {
							min: Element.calculateGroupMin(zGroups[firstGroupIndex - 1]),
							max: Element.calculateGroupMax(zGroups[firstGroupIndex - 1])
						}
						: null;
				segGroups.forEach((group: Array<ProductGroup>, idx: number) => {
					let align: AlignmentRules = Element.alignmentRules(segGroups, idx, segment),
						beginPoint: number = !idx ? align[stand.alignment].startPoint(prevProduct) : align[stand.alignment].nextPoint();

					// set default angle for product group
					group.forEach((pg: ProductGroup) => {
						pg.angle = GlobalSettings.DEFAULT_PRODUCT_GROUP_ANGLE;
					});

					// set correct x coordinate for product group
					group.forEach((pg: ProductGroup) => {
						pg.begin.x = isFinite(beginPoint) ? beginPoint : align[stand.alignment].startPoint();
					});

					// set correct z coordinate for product group
					group
						.filter((pg: ProductGroup) => !pg.dependProduct)
						.sort((a: ProductGroup, b: ProductGroup) => a.getBoundingZ().max - b.getBoundingZ().max || 1)
						.forEach((pg: ProductGroup, idx: number, list: Array<ProductGroup>) => {
							pg.begin.y = pg.element.depend.getBoundingY().max;
							pg.begin.z = Utils.round((!idx ? pg.element.layoutBoundingZ.min :
								list[idx - 1].getBoundingZ().max) + pg.Depth);
						});

					group
						.filter((pg: ProductGroup) => pg.dependProduct)
						.sort((a: ProductGroup, b: ProductGroup) => a.begin.y - b.begin.y)
						.forEach((pg: ProductGroup) => {
							pg.begin.y = pg.dependProduct.getBoundingY().max;
							pg.begin.z = pg.dependProduct.getBoundingZ().max;
						});
				});
			});
		}

		// highlight product group when error
		zGroups.forEach((group: Array<ProductGroup>) => {
			group.forEach((pg: ProductGroup) => {
				pg.error = false;

				const errors: { [key in ProductErrors]?: boolean } = {
					[ProductErrors.BoxDepth]: pg.getBoundingZ().max > pg.element.layoutBoundingZ.max
					|| pg.getBoundingZ().min < pg.element.layoutBoundingZ.min,
					[ProductErrors.BoxLeftLimit]: pg.getBoundingX().min < boxLimits.min,
					[ProductErrors.BoxRightLimit]: pg.getBoundingX().max > boxLimits.max,
					[ProductErrors.ElementHeight]: (this.depend as ShelfElement).hasShelfIntersections(pg),
					[ProductErrors.ProductIntersection]: this.intersectsProduct(pg)
				};

				if (pg.dependProduct) {
					errors[ProductErrors.DependLimits] = pg.dependProduct.Width < pg.Width;
					errors[ProductErrors.DependDepth] = pg.dependProduct.Depth < pg.Depth;
				}

				pg.error = Object.values(errors).some((error: boolean) => error);
				pg.errorTypes = Object.keys(errors).filter((key: ProductErrors) => errors[key]);
			});
		});
	}

	public canPutProductGroups(products: Array<ProductGroup>, point: Point): ICanPutProductsResult {
		const block: Block = this.stand.findBlock(point.x, point.y, products[0].Width);

		for (let product of products) {
			if (block && (product.block != block.id || !block.id)) {
				if (!block.isProductSuitable(product.product)) {
					return {
						success: false,
						message: {
							messageText: 'BLOCK_PRODUCT_ERROR',
							translateParams: { blockName: block.name }
						}
					};
				}
			}
		}

		return {
			success: true,
			message: {
				messageText: '',
				translateParams: {}
			}
		}
	}

	protected findLowerProduct(products: Array<ProductGroup>, list: Array<Array<ProductGroup>>, point: Point): ProductGroup {
		return null;
	}

	protected removePreviousProducts(group: Array<ProductGroup>): void {
		if (group[0].element != this && this.isNeedRemoveProducts(group[0])) {
			this.stand.products = this.stand.products.filter((pg: ProductGroup) => pg.element != this);
		}
	}

	protected findPriorProduct(group: Array<ProductGroup>, list: Array<Array<ProductGroup>>, point: Point): ProductGroup {
		if (list.length == 1) {
			const bounding: Bounding = { min: point.x, max: Utils.round(point.x + group[0].Width) };
			const sameXProducts: Array<Array<ProductGroup>> = this.findSameXProducts(group, bounding);

			if (sameXProducts.length) {
				return sameXProducts[0]
					.sort((a: ProductGroup, b: ProductGroup) => b.getBoundingZ().min - a.getBoundingZ().min)
					.find((pg: ProductGroup) => pg.getBoundingZ().max < point.z);
			}
		}

		return null;
	}

	protected adjustPointToPriorProduct(priorPg: ProductGroup, group: Array<ProductGroup>, point: Point): void {
		const groupDepth: number = group
			.filter((pg: ProductGroup) => !pg.dependProduct)
			.reduce((depth: number, pg: ProductGroup) => Utils.round(depth + pg.Depth), 0);

		point.x = priorPg.begin.x;
		point.y = this.getBoundingY().min;
		point.z = Utils.round(priorPg.getBoundingZ().max + groupDepth);
	}

	protected getItemsZCoordinates(productGroups: Array<ProductGroup>, product: ProductGroup): Array<Array<Point>> {
		const productCoordinates: Array<Array<Point>> = super.getItemsZCoordinates(productGroups, product);
		return [...productCoordinates, this.layoutZCoordinates];
	}
}
